import { useEffect, useCallback, useState } from 'react';

import {
  TFormValues,
  TGroupOption,
  THookReturn,
  TUseFormGroupProps,
} from '../react/types';
import { TFactoryOptions } from '../../core/types';

import { CoreEvents, ALL_NAMESPACE_EVENTS } from '@core';
import { getFormInstance, getGroupFormsIds } from '@core/managers';
import { EEVents } from '@core/constants';

const useFormGroup = ({
  group,
  onData,
  onClick,
  onSubmit,
  ids = [],
}: TUseFormGroupProps): THookReturn => {
  const [formIds, setFormIds] = useState(group ? getGroupFormsIds(group) : ids);

  const onDataChange = useCallback(() => {
    const data = formIds.reduce(
      (acc, formId) => ({
        ...acc,
        [formId]: getFormInstance(formId).formData,
      }),
      {},
    );

    onData && onData(data);
  }, [onData]);

  function extractFormsData<T>(opts?: TFactoryOptions & TGroupOption): T {
    return formIds.reduce(
      (acc, formId) => {
        if (opts?.aggregate) {
          return Object.keys(acc).reduce(
            (innerAcc, key) => ({
              ...innerAcc,
              [key]: {
                ...acc[key],
                ...getFormInstance(formId, opts).formData[key],
              },
            }),
            {},
          );
        }

        return { ...acc, [formId]: getFormInstance(formId, opts).formData };
      },
      {
        formatted: {},
        erroredFields: {},
        fields: {},
        form: {},
        predictableErroredFields: {},
        filteredFields: {},
      } as TFormValues,
    ) as T;
  }

  useEffect(() => {
    if (formIds.length || !group) return;
    setFormIds(getGroupFormsIds(group));
  }, [formIds]);

  useEffect(() => {
    formIds.map((formId) =>
      getFormInstance(formId).subscribe(EEVents.ON_FIELD_CHANGE, onDataChange),
    );
  }, [onData, formIds]);

  useEffect(() => {
    formIds.map((formId) =>
      getFormInstance(formId).subscribe(EEVents.ON_SCOPE_CHANGE, onDataChange),
    );
  }, [onData, formIds]);

  useEffect(() => {
    formIds.map((formId) =>
      getFormInstance(formId).subscribe(
        ALL_NAMESPACE_EVENTS(CoreEvents.ON_FIELD_REHYDRATE),
        onDataChange,
      ),
    );
  }, [onData, formIds]);

  useEffect(() => {
    formIds.map((formId) =>
      getFormInstance(formId).subscribe(
        ALL_NAMESPACE_EVENTS(CoreEvents.ON_FIELD_CLICK),
        () =>
          onClick &&
          onClick(extractFormsData<TFormValues>({ aggregate: true })),
      ),
    );
  }, [onClick, formIds]);

  useEffect(() => {
    formIds.map((formId) =>
      getFormInstance(formId).subscribe(
        CoreEvents.ON_FORM_SUBMIT,
        () => onSubmit && onSubmit(extractFormsData({})),
      ),
    );
  }, [onSubmit, formIds]);

  const submitForm = () =>
    formIds.map((formId) =>
      getFormInstance(formId).publish(CoreEvents.ON_FORM_SUBMIT),
    );

  return {
    submitForm,
    formData: extractFormsData,
  };
};

export { useFormGroup };
